import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  login as loginRequest,
  refreshAccessToken,
} from 'services/auth';

const AUTH_STORAGE_KEY = 'jplus-shop-auth';
const ACCESS_TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

interface IAuthState {
  accessToken: string;
  refreshToken: string;
  userId: number;
  isRestaurant: boolean;
}

interface IAuthContext {
  auth: IAuthState | null;
  isAuthenticated: boolean;
  isRestaurant: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): void;
}

const AuthContext = createContext<IAuthContext | undefined>(undefined);

const decodeBase64Url = (value: string) => {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddedValue = normalizedValue.padEnd(
    normalizedValue.length + ((4 - (normalizedValue.length % 4)) % 4),
    '='
  );

  return window.atob(paddedValue);
};

const getAccessTokenExpiresAt = (accessToken: string) => {
  try {
    const [, payload] = accessToken.split('.');

    if (!payload) {
      return null;
    }

    const decodedPayload = JSON.parse(decodeBase64Url(payload));

    return typeof decodedPayload.exp === 'number'
      ? decodedPayload.exp * 1000
      : null;
  } catch (error) {
    return null;
  }
};

const shouldRefreshAccessToken = (accessToken: string) => {
  const expiresAt = getAccessTokenExpiresAt(accessToken);

  if (!expiresAt) {
    return false;
  }

  return expiresAt - Date.now() <= ACCESS_TOKEN_REFRESH_BUFFER_MS;
};

const readStoredAuth = () => {
  try {
    const storedAuth = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return storedAuth ? (JSON.parse(storedAuth) as IAuthState) : null;
  } catch (error) {
    return null;
  }
};

const writeStoredAuth = (auth: IAuthState) => {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
};

const clearStoredAuth = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
};

const AuthProvider: FC = (props) => {
  const [storedAuth] = useState<IAuthState | null>(readStoredAuth);
  const [auth, setAuth] = useState<IAuthState | null>(() =>
    storedAuth && !shouldRefreshAccessToken(storedAuth.accessToken)
      ? storedAuth
      : null
  );
  const [isRestoringAuth, setIsRestoringAuth] = useState(
    () =>
      Boolean(storedAuth?.refreshToken) &&
      Boolean(storedAuth?.accessToken) &&
      shouldRefreshAccessToken(storedAuth!.accessToken)
  );

  const persistAuth = useCallback((nextAuth: IAuthState) => {
    writeStoredAuth(nextAuth);
    setAuth(nextAuth);
  }, []);

  const clearAuth = useCallback(() => {
    clearStoredAuth();
    setAuth(null);
  }, []);

  const refreshAuth = useCallback(
    async (currentAuth: IAuthState) => {
      const response = await refreshAccessToken(currentAuth.refreshToken);
      const nextAuth = {
        ...currentAuth,
        accessToken: response.access,
        refreshToken: response.refresh || currentAuth.refreshToken,
      };

      persistAuth(nextAuth);

      return nextAuth;
    },
    [persistAuth]
  );

  useEffect(() => {
    if (!storedAuth || !isRestoringAuth) {
      return;
    }

    refreshAuth(storedAuth)
      .catch(clearAuth)
      .finally(() => setIsRestoringAuth(false));
  }, [clearAuth, isRestoringAuth, refreshAuth, storedAuth]);

  useEffect(() => {
    if (!auth) {
      return undefined;
    }

    const expiresAt = getAccessTokenExpiresAt(auth.accessToken);

    if (!expiresAt) {
      return undefined;
    }

    const refreshDelay = Math.max(
      expiresAt - Date.now() - ACCESS_TOKEN_REFRESH_BUFFER_MS,
      0
    );
    const refreshTimeout = window.setTimeout(() => {
      refreshAuth(auth).catch(clearAuth);
    }, refreshDelay);

    return () => window.clearTimeout(refreshTimeout);
  }, [auth, clearAuth, refreshAuth]);

  const login = useCallback(async (username: string, password: string) => {
    const response = await loginRequest(username, password);
    const nextAuth = {
      accessToken: response.access,
      refreshToken: response.refresh,
      userId: response.user_id,
      isRestaurant: response.is_restaurant,
    };

    persistAuth(nextAuth);
  }, [persistAuth]);

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const value = useMemo(
    () => ({
      auth,
      isAuthenticated: Boolean(auth?.accessToken),
      isRestaurant: Boolean(auth?.isRestaurant),
      login,
      logout,
    }),
    [auth, login, logout]
  );

  if (isRestoringAuth) {
    return null;
  }

  return <AuthContext.Provider value={value} {...props} />;
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuth };
